import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate, useParams, Link} from "react-router-dom";
import {Breadcrumb, Button, Form, Modal, Progress, Slider, Switch, message, Tooltip} from "antd";
import {useCookies} from "react-cookie";
import dayjsInstance from "../utils/dayjs";
import SpeechRecognition, {useSpeechRecognition} from 'react-speech-recognition';
import axios from "../utils/axios";
import { textToIPAPure } from "../common/ipaPure";
import {
    AudioFilled, SoundOutlined,
    StepBackwardOutlined, StepForwardOutlined, PlayCircleFilled, PauseCircleFilled,
    SettingOutlined, HomeOutlined, CheckOutlined, EyeOutlined, EyeInvisibleOutlined,
    RetweetOutlined, FontSizeOutlined, VideoCameraOutlined, CheckCircleOutlined
} from "@ant-design/icons";
import repeatTrack from "../component/icon/repeat-track.png";
import convertTimeToMilliseconds from "../utils/convertTimeToMilliseconds";
import toLowerCamelCase from "../utils/toLowerCamelCase";
import {createUserListening, getUserListening} from "../apis/userListening";
import { alignTranscript } from "../utils/alignTranscript";

export default function Listening() {
    const {slug} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const index = location?.state?.index;
    const [form] = Form.useForm();
    const audioElem = useRef();
    const videoElem = useRef();
    const lyricsContainerRef = useRef();

    const [dataLesson, setdataLesson] = useState({});
    const [options, setOptions] = useState([]);
    const [story, setStory] = useState("Main Story");
    const [dataUserListening, setDataUserListening] = useState([]);
    // removed modal state for transcript checking
    const [isPlaying, setisPlaying] = useState(false);
    const [currentTime, setcurrentTime] = useState(0);
    const [speedMode, setSpeedMode] = useState(1);
    const [active, setActive] = useState(0);
    const [playAll, setPlayAll] = useState(false);
    const [timestamps, setTimestamps] = useState({});
    const [duration, setDuration] = useState(0);
    const [count, setCount] = useState(1);
    const [replay, setReplay] = useState(1);
    const [timeReplay, setTimeReplay] = useState(0.5);
    const [timeoutPlay, setTimeoutPlay] = useState(null);
    const [lyric, setLyric] = useState([]);
    const [showSettings, setShowSettings] = useState(true);
    const [totalDuration, setTotalDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [subtitleFontSize, setSubtitleFontSize] = useState(16);
    const [translationFontSize, setTranslationFontSize] = useState(14);
    const [showTranslation, setShowTranslation] = useState(true);
    const [autoScroll, setAutoScroll] = useState(true);
    const isSeekingRef = useRef(false);
    const shadowReadingRef = useRef(false);
    const fullTranscriptRef = useRef(''); // accumulates all speech during shadow-reading
    const lastTranscriptRef = useRef('');
    const autoAdvanceLSTimerRef = useRef(null); // timer for lyric auto-advance (no-audio fallback)
    const playSentenceIntervalRef = useRef(null); // interval for per-sentence playback stop
    const sentenceRepeatedRef = useRef({}); // whether sentence has been repeated once during current play
    const lyricRef = useRef(lyric);
    const activeRef = useRef(active);
    const speedModeRef = useRef(speedMode);
    useEffect(() => { lyricRef.current = lyric; }, [lyric]);
    useEffect(() => { activeRef.current = active; }, [active]);
    useEffect(() => { speedModeRef.current = speedMode; }, [speedMode]);

    
    const [translations, setTranslations] = useState([]);
    const [showIPA, setShowIPA] = useState(false);
    const [translationLoading, setTranslationLoading] = useState(false);
    // IPA cache: compute lazily for visible lines, cache results to avoid recompute
    const ipaCacheRef = useRef(new Map());
    const getIpaForIndex = (i) => {
        const line = (lyric && lyric[i]) || '';
        return ipaCacheRef.current.has(line) ? ipaCacheRef.current.get(line) : null;
    };

    const scheduleComputeIpa = (indices) => {
        if (!Array.isArray(lyric)) return;
        // compute in small async batches to avoid blocking
        const toCompute = [];
        for (const i of indices) {
            const line = lyric[i] || '';
            if (!ipaCacheRef.current.has(line)) toCompute.push({ i, line });
        }
        if (!toCompute.length) return;
            // use setTimeout to yield to UI thread and request IPA from backend API
            setTimeout(async () => {
                try {
                    // Compute IPA locally in the frontend using pure-JS converter
                    for (const { line } of toCompute) {
                        try { ipaCacheRef.current.set(line, textToIPAPure(line || '')); } catch (err) { ipaCacheRef.current.set(line, ''); }
                    }
                } catch (e) {
                    for (const { line } of toCompute) {
                        ipaCacheRef.current.set(line, '');
                    }
                }
                // trigger render
                try { setTranslations(t => [...t]); } catch (e) {}
            }, 20);
    };
    const [ipaReady, setIpaReady] = useState(false);
    const [goalDays, setGoalDays] = useState(30);
    const [goalHours, setGoalHours] = useState(30);
    const [editingGoals, setEditingGoals] = useState(false);
    // Shadow-reading mode: play audio + listen simultaneously, lyric follows audio
    const [isBatchScoring, setIsBatchScoring] = useState(false);
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [scoreResult, setScoreResult] = useState(null); // { avgPoint, lines: [{lyric, text, point}] }
    const [playingSentence, setPlayingSentence] = useState(null); // index of sentence being played solo
    // Control visibility of transcript globally and per-line
    const [showTranscript, setShowTranscript] = useState(true); // global toggle to show/hide transcript
    const [hiddenLines, setHiddenLines] = useState({}); // map of lineIndex -> true if hidden
    const [loopMode, setLoopMode] = useState(0); // 0: no loop, 1: loop single, 2: loop whole
    // When user enables "lặp 1 câu", ensure current active sentence is allowed to repeat
    useEffect(() => {
        if (loopMode === 1) {
            try { sentenceRepeatedRef.current[active] = false; } catch (e) {}
        }
    }, [loopMode, active]);
    const [showDrillMode, setShowDrillMode] = useState(false); // "Luyện nghe" drill mode
    const [drillIndex, setDrillIndex] = useState(0); // current sentence in drill mode
    const [userInfo, setUserInfo] = useState(null); // fresh user data from API (vip_expire_at)
    const [drillIsPlaying, setDrillIsPlaying] = useState(false); // audio playing in drill mode
    const [drillAnswer, setDrillAnswer] = useState(''); // user typed answer in drill mode
    const [drillAnswerResult, setDrillAnswerResult] = useState(null); // { point, words } after check
    const [drillListening, setDrillListening] = useState(false); // voice input active for drill mode
    const [drillRecording, setDrillRecording] = useState(false); // audio recording in drill mode
    const [drillVideoRecording, setDrillVideoRecording] = useState(false); // video recording in drill mode
    const [drillMediaRecorder, setDrillMediaRecorder] = useState(null); // MediaRecorder for drill mode
    const [drillRecordingTranscript, setDrillRecordingTranscript] = useState(''); // accumulated transcript during recording
    const [showDrillRecordingModal, setShowDrillRecordingModal] = useState(false); // show result after recording
    const drillCameraPreviewRef = useRef(null); // camera preview for drill mode
    const drillCameraStreamRef = useRef(null); // camera stream for drill mode
    const perSentenceVideoRecorderRef = useRef(null);
    const perSentenceCameraStreamRef = useRef(null);
    const [perSentenceVideoMode, setPerSentenceVideoMode] = useState(false);
    
    const { transcript: drillTranscript, listening: isDrillMicActive, resetTranscript: resetDrillTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

    // Ensure drill camera stream is assigned to video element after render
    useEffect(() => {
        if (drillVideoRecording && drillCameraStreamRef.current && drillCameraPreviewRef.current) {
            if (drillCameraPreviewRef.current.srcObject !== drillCameraStreamRef.current) {
                drillCameraPreviewRef.current.srcObject = drillCameraStreamRef.current;
                drillCameraPreviewRef.current.play().catch(err => console.log('Video play handled:', err));
            }
        }
    }, [drillVideoRecording]);

    const [showDrillSettings, setShowDrillSettings] = useState(true); // settings panel in drill mode
    const [drillAutoNext, setDrillAutoNext] = useState(false); // auto-advance to next sentence after playback
    const [drillRepeat, setDrillRepeat] = useState(1); // number of times to repeat each sentence (1 = no repeat)
    const drillRepeatCountRef = useRef(0); // tracks how many times current sentence has played
    const drillIntervalRef = useRef(null); // interval for stopping drill playback at sentence end
    const drillEndRef = useRef(null); // end time (seconds) of current drill sentence
    const drillAutoNextRef = useRef(drillAutoNext);
    const drillRepeatRef = useRef(drillRepeat);
    const drillIndexRef = useRef(0); // mirror of drillIndex for use inside intervals
    useEffect(() => { drillAutoNextRef.current = drillAutoNext; }, [drillAutoNext]);
    useEffect(() => { drillRepeatRef.current = drillRepeat; }, [drillRepeat]);

    // Sync voice transcript to drill answer input
    useEffect(() => {
        if (drillListening && drillTranscript) {
            setDrillAnswer(drillTranscript);
            // Accumulate transcript during video recording
            if (drillVideoRecording || drillRecording) {
                setDrillRecordingTranscript(prev => {
                    const newText = drillTranscript.trim();
                    if (!prev || !newText) return newText;
                    // Append new words that aren't already at the end
                    const prevWords = prev.toLowerCase().split(/\s+/);
                    const newWords = newText.toLowerCase().split(/\s+/);
                    const lastPrevWord = prevWords[prevWords.length - 1];
                    const firstNewWord = newWords[0];
                    if (firstNewWord === lastPrevWord) {
                        // Likely continuation, merge
                        return prev + ' ' + newText.split(/\s+/).slice(1).join(' ');
                    }
                    return prev + ' ' + newText;
                });
            }
        }
    }, [drillTranscript, drillListening, drillVideoRecording, drillRecording]);

    // Stop listening when isDrillMicActive becomes false
    useEffect(() => {
        if (!isDrillMicActive && drillListening) {
            setDrillListening(false);
        }
    }, [isDrillMicActive, drillListening]);

    const speeds = [0.5, 0.75, 0.8, 0.9, 1, 1.25, 1.5, 2];

    const isAudioUrl = (url) => {
        if (!url) return false;
        if (typeof url !== 'string') return false;
        return /\.(mp3|m4a|wav|ogg|webm|aac)(\?.*)?$/i.test(url) || /^data:audio\//.test(url);
    };

    const isVideoUrl = (url) => {
        if (!url) return false;
        if (typeof url !== 'string') return false;
        return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url) || /^data:video\//.test(url);
    };

    const resolveVideo = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return isVideoUrl(val) ? val : null;
        if (typeof val === 'object') {
            if (Array.isArray(val) && val.length) {
                const first = val[0];
                if (typeof first === 'string') return isVideoUrl(first) ? first : null;
                if (first && typeof first.url === 'string') return isVideoUrl(first.url) ? first.url : null;
            }
            if (typeof val.url === 'string') return isVideoUrl(val.url) ? val.url : null;
        }
        return null;
    };

    const proxyIfR2 = (u) => {
        if (!u || typeof u !== 'string') return u;
        if (u.includes('.r2.cloudflarestorage.com')) {
            const proxyBase = (process.env.REACT_APP_API_URL || '');
            return `${proxyBase.replace(/\/$/, '')}/media/proxy?url=${encodeURIComponent(u)}`;
        }
        return u;
    };

    const resolveAudio = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return isAudioUrl(val) ? val : null;
        if (typeof val === 'object') {
            if (Array.isArray(val) && val.length) {
                const first = val[0];
                if (typeof first === 'string') return isAudioUrl(first) ? first : null;
                if (first && typeof first.url === 'string') return isAudioUrl(first.url) ? first.url : null;
            }
            if (typeof val.url === 'string') return isAudioUrl(val.url) ? val.url : null;
        }
        return null;
    };

    const getAudioSrc = () => {
        const src = story === "Main Story" ? resolveAudio(dataLesson?.mainStoryAudio)
            : story === "Vocabulary" ? resolveAudio(dataLesson?.vocabularyAudio)
            : story === "Mini Story" ? resolveAudio(dataLesson?.miniStoryAudio)
            : story === "POV" ? resolveAudio(dataLesson?.POVAudio)
            : story === "Comment" ? resolveAudio(dataLesson?.commentAudio)
            : null;
        if (src) return proxyIfR2(src);
        // Fallback: scan raw payload for any audio-like field
        try {
            const raw = dataLesson?._raw || {};
            for (const v of Object.values(raw)) {
                if (typeof v === 'string' && isAudioUrl(v)) return proxyIfR2(v);
                if (Array.isArray(v) && v.length && typeof v[0] === 'string' && isAudioUrl(v[0])) return proxyIfR2(v[0]);
                if (v && typeof v === 'object' && typeof v.url === 'string' && isAudioUrl(v.url)) return proxyIfR2(v.url);
            }
        } catch (e) {}
        return null;
    };

    const getVideoSrc = () => {
        const src = story === "Main Story" ? resolveVideo(dataLesson?.mainStoryVideo)
            : story === "Vocabulary" ? resolveVideo(dataLesson?.vocabularyVideo)
            : story === "Mini Story" ? resolveVideo(dataLesson?.miniStoryVideo)
            : story === "POV" ? resolveVideo(dataLesson?.POVVideo)
            : story === "Comment" ? resolveVideo(dataLesson?.commentVideo)
            : null;
        if (src) return proxyIfR2(src);
        try {
            const raw = dataLesson?._raw || {};
            for (const v of Object.values(raw)) {
                if (typeof v === 'string' && isVideoUrl(v)) return proxyIfR2(v);
                if (Array.isArray(v) && v.length && typeof v[0] === 'string' && isVideoUrl(v[0])) return proxyIfR2(v[0]);
                if (v && typeof v === 'object' && typeof v.url === 'string' && isVideoUrl(v.url)) return proxyIfR2(v.url);
            }
        } catch (e) {}
        return null;
    };

    const getMediaElem = () => {
        const v = getVideoSrc();
        if (v && videoElem.current) return videoElem.current;
        return audioElem.current;
    };

    // Keep media element volume in sync with UI
    useEffect(() => {
        try {
            const m = getMediaElem();
            if (m) m.volume = Number.isFinite(volume) ? volume : 1;
        } catch (e) {}
    }, [volume]);

    const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds) || !isFinite(seconds) || seconds <= 0) return '0:00';
        const s = Math.floor(seconds);
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
        return `${m}:${String(sec).padStart(2, '0')}`;
    };

    const getFirstAudioUrl = (normalized) => {
        if (!normalized) return null;
        const fields = ['mainStoryAudio','vocabularyAudio','miniStoryAudio','POVAudio','commentAudio'];
        for (const f of fields) {
            const v = normalized[f];
            const a = resolveAudio(v);
            if (a) return a;
        }
        // fallback: scan raw payload for any audio-like value
        try {
            const raw = normalized._raw || {};
            for (const val of Object.values(raw)) {
                if (typeof val === 'string' && isAudioUrl(val)) return val;
                if (Array.isArray(val) && val.length && typeof val[0] === 'string' && isAudioUrl(val[0])) return val[0];
                if (val && typeof val === 'object' && typeof val.url === 'string' && isAudioUrl(val.url)) return val.url;
            }
        } catch (e) {}
        return null;
    };

    const fetchFirstAudioDuration = (url) => {
        try {
            if (!url) return;
            const a = new Audio();
            a.preload = 'metadata';
            a.crossOrigin = 'anonymous';
            a.src = url;
            const onLoaded = () => {
                try { const d = a.duration; if (d && isFinite(d) && !isNaN(d) && d > 0) setTotalDuration(prev => (prev !== d ? d : prev)); } catch (e) {}
                try { a.removeEventListener('loadedmetadata', onLoaded); a.src = ''; } catch (e) {}
            };
            const onError = () => { try { a.removeEventListener('error', onError); a.src = ''; } catch (e) {} };
            a.addEventListener('loadedmetadata', onLoaded);
            a.addEventListener('error', onError);
        } catch (e) { /* ignore */ }
    };

    const [cookies] = useCookies(["user"]);
    const { transcript, listening } = useSpeechRecognition();

    // Always skip calling server-side translation ensure endpoint from Listening page
    // (we don't want Listening view to trigger background translation jobs)
    const skipEnsureOnThisPage = true;

    const fetchUserInfo = async () => {
        if (!cookies?.user?.token) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/me`, {
                headers: {
                    'Authorization': `Bearer ${cookies?.user?.token}`
                }
            });
            setUserInfo(response.data);
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    const isAdmin = () => {
        try {
            if (userInfo && userInfo.role === 1) return true;
            if (cookies && cookies.user && cookies.user.role === 1) return true;
        } catch (e) {
            /* ignore */
        }
        return false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { lesson(); userListening(); fetchUserInfo(); }, []);

    // Load per-lesson goals from localStorage when lesson loads
    useEffect(() => {
        if (!dataLesson?.id) return;
        try {
            const key = `lesson_goal_${dataLesson.id}`;
            const raw = localStorage.getItem(key);
            if (raw) {
                const obj = JSON.parse(raw);
                if (obj?.days) setGoalDays(Number(obj.days));
                if (obj?.hours) setGoalHours(Number(obj.hours));
            } else {
                // defaults
                setGoalDays(30); setGoalHours(30);
            }
        } catch (e) { console.error('Load goals error', e); }
    }, [dataLesson?.id]);

    const saveGoalsForLesson = (days, hours) => {
        if (!dataLesson?.id) return;
        const key = `lesson_goal_${dataLesson.id}`;
        const obj = { days: Number(days || 0), hours: Number(hours || 0) };
        try { localStorage.setItem(key, JSON.stringify(obj)); setGoalDays(obj.days); setGoalHours(obj.hours); message.success('Đã lưu mục tiêu'); }
        catch (e) { console.error('Save goal error', e); message.error('Không thể lưu mục tiêu'); }
    };

    // Auto-open drill mode if navigated from Speaking page
    useEffect(() => {
        if (location?.state?.openDrillMode && dataLesson?.id && lyric?.length > 0 && !showDrillMode) {
            // Check VIP access before opening drill mode
            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
            const hasVipAccess = (dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD") && vipExpireAt !== null);
            if (hasVipAccess) {
                // Small delay to ensure audio and data are ready
                setTimeout(() => openDrillMode(), 500);
            } else {
                message.warning('Chức năng Luyện nghe dành cho người dùng VIP. Vui lòng nâng cấp tài khoản!');
            }
        }
    }, [dataLesson, lyric, location?.state?.openDrillMode, showDrillMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup auto-advance timer on unmount
    useEffect(() => {
        return () => {
            clearAutoAdvanceLS();
            if (playSentenceIntervalRef.current) clearInterval(playSentenceIntervalRef.current);
            if (drillIntervalRef.current) clearInterval(drillIntervalRef.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Allow unauthenticated users to view listening page.
        // Only apply lesson access checks when user is authenticated.
        if (!cookies?.user) {
            if (index === undefined) navigate('/');
            return;
        }

        if (dataUserListening?.lesson_id) {
            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
            if ((dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD")
                && vipExpireAt !== null)
                || +dataUserListening?.lesson_id >= +dataLesson.id || +index === 0) {
                // allowed
            } else {
                navigate('/');
            }
        }
        if (index === undefined) navigate('/');
    }, [cookies, userInfo]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        addTimestamps();
        const opts = [];
        if (dataLesson?.mainStory?.trim()) opts.push('Main Story');
        if (dataLesson?.vocabulary?.trim()) opts.push('Vocabulary');
        if (dataLesson?.miniStory?.trim()) opts.push('Mini Story');
        if (dataLesson?.POV?.trim()) opts.push('POV');
        if (dataLesson?.comment?.trim()) opts.push('Comment');
        setOptions(opts);
    }, [dataLesson]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        try {
            if (duration > 0 && count <= replay && !playAll) {
                const timeoutPlayId = setTimeout(() => {
                    safePlay().then(() => {
                        try { audioElem.current.pause(); } catch(e) {}
                    });
                    if (count === replay) {
                        setisPlaying(false); setDuration(0); setCount(1);
                    } else {
                        const timeout = setTimeout(async () => {
                            await play(active); setCount(count + 1); clearTimeout(timeout);
                        }, timeReplay * 1000);
                    }
                    clearTimeout(timeoutPlayId);
                }, duration * 1000 / speedMode);
                setTimeoutPlay(timeoutPlayId);
            }
        } catch (e) { console.log(e); }
    }, [duration, count]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        form.resetFields();
        // Auto-scroll to active lyric line
        if (autoScroll && lyricsContainerRef.current) {
            const activeEl = lyricsContainerRef.current.querySelector(`[data-lyric-index="${active}"]`);
            if (activeEl) {
                activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [active]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { handleLyric(toLowerCamelCase(story)); }, [story, dataLesson]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (listening) {
            // Live: show transcript in textarea (non-shadow only)
            if (!shadowReadingRef.current) {
                form.setFieldsValue({ text: transcript });
            } else {
                // accumulate incremental deltas into fullTranscriptRef to preserve all spoken words
                try {
                    const current = transcript || '';
                    const last = lastTranscriptRef.current || '';
                    if (current.length > last.length) {
                        const delta = current.slice(last.length).trim();
                        if (delta) fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + ' ' : '') + delta;
                    }
                    lastTranscriptRef.current = current;
                    try { SpeechRecognition.resetTranscript(); } catch (e) {}
                } catch (e) { console.error('shadow accumulate error', e); }
            }
        } else if (!listening && transcript) {
            // Stopped — finalize any remaining transcript delta
            try {
                const current = transcript || '';
                const last = lastTranscriptRef.current || '';
                if (current.length > last.length) {
                    const delta = current.slice(last.length).trim();
                    if (delta) fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + ' ' : '') + delta;
                }
            } catch (e) { console.error('finalize shadow transcript error', e); }
            lastTranscriptRef.current = '';
            // When not in shadow-reading mode (or drill), auto-run the check handler
            if (!shadowReadingRef.current && !drillRecording && !drillVideoRecording) {
                try { handleCheckShadow(); } catch (e) { console.error('auto check shadow error', e); }
            } else if (!shadowReadingRef.current && (drillRecording || drillVideoRecording)) {
                // no-op: drill recording flow handles scoring elsewhere
            } else {
                // If shadow-reading just finished (we set shadowReadingRef false before stop), run check
                try { handleCheckShadow(); } catch (e) { console.error('auto check shadow error', e); }
            }
        }
    }, [listening, transcript]);

    // Reset totalDuration when track or timestamps change, but preserve stored translations
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setTotalDuration(0);
        // Preserve translations if lesson record already contains them for this track
        try {
            const expectedLen = Array.isArray(lyric) ? lyric.length : 0;
            const tRaw = dataLesson?.translations;
            const found = (tRaw && typeof tRaw === 'object') ? findTranslationsForTrack(tRaw, story, expectedLen) : null;
            if (found && expectedLen > 0) {
                setTranslations(sanitizeTranslationsArray(found));
            } else if (translations.length !== expectedLen) {
                setTranslations(Array.from({ length: expectedLen }, () => ''));
            }
        } catch (e) {
            setTranslations([]);
        }

        // Delay so audio element has time to mount
        const timer = setTimeout(() => {
            handleDurationUpdate();
        }, 500);
        return () => clearTimeout(timer);
    }, [story, timestamps]);

    // Fetch translations whenever lyric changes (always enabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log('Translation useEffect triggered (always on):', {
            lyricLength: lyric?.length,
            translationsLength: translations.length,
            shouldFetch: lyric?.length > 0 && (translations.length === 0 || translations.length !== lyric.length)
        });
        if (lyric?.length > 0) {
            if (translations.length === 0 || translations.length !== lyric.length) {
                console.log('Fetching translations for', lyric.length, 'lines...');
                fetchTranslations();
            }
            // If translations missing in DB for this lesson track, request server to ensure them
            try {
                const trackKey = toLowerCamelCase(story);
                const lessonHasTranslations = dataLesson?.translations && (Array.isArray(dataLesson.translations) ? dataLesson.translations.length === lyric.length : (dataLesson.translations && Array.isArray(dataLesson.translations[trackKey]) && dataLesson.translations[trackKey].length === lyric.length));
                if (!lessonHasTranslations && dataLesson?.id) {
                    console.log('Requesting server to ensure translations for lesson', dataLesson.id);
                    if (skipEnsureOnThisPage) {
                        console.log('Skipping ensureOne because current path is Speaking');
                    } else if (isAdmin()) {
                        axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id }, { timeout: 60000 })
                            .then(() => {
                                // re-fetch lesson to pick up saved translations
                                setTimeout(() => { lesson(); }, 800);
                            })
                            .catch(err => console.error('ensureOne error', err));
                    } else {
                        console.log('Skipping ensureOne: user not admin');
                    }
                }
            } catch (e) { console.error('ensure translations trigger error', e); }
        }
    }, [lyric]);

    // IPA is computed via `computedIpaData` (memoized) to avoid flicker/repeated work

    const fetchTranslations = async () => {
        if (!lyric || lyric.length === 0) {
            console.log('fetchTranslations: No lyric data');
            return;
        }
        // If lesson already has stored translations from backend, prefer those.
        // Backend stores translations as an object with track keys; detect and use current track.
        try {
            const t = dataLesson?.translations;
            const trackTrans = (t && typeof t === 'object') ? findTranslationsForTrack(t, story, lyric.length) : null;
                if (trackTrans) {
                setTranslations(sanitizeTranslationsArray(trackTrans));
                console.log('Using stored translations from lesson record for', story);
                return;
            }
        } catch (e) { console.error('check stored translations error', e); }

        console.log('fetchTranslations: Starting fetch for', lyric.length, 'lines');
        console.log('API URL:', `${process.env.REACT_APP_API_URL}/translation/batch`);
        
        setTranslationLoading(true);
        try {
            const payload = { lines: lyric, from: 'en', to: 'vi' };
            console.log('Translation request payload:', payload);
            
            const result = await axios.post(
                `${process.env.REACT_APP_API_URL}/translation/batch`,
                payload,
                { timeout: 30000 } // Increased timeout to 30s
            );
            
            console.log('Translation API response:', result?.data);
            
                if (result?.data?.translations && Array.isArray(result.data.translations) && result.data.translations.length === lyric.length) {
                const backendArr = sanitizeTranslationsArray(result.data.translations);
                const hasNonEmpty = backendArr.some(t => typeof t === 'string' && t.trim().length > 0);
                if (hasNonEmpty) {
                    setTranslations(backendArr);
                    console.log('✅ Translations loaded successfully:', backendArr.length, 'lines');
                    console.log('Sample translations:', backendArr.slice(0, 3));
                } else {
                    console.warn('⚠️ Backend returned translations array but entries are empty — requesting server to ensure translations');
                    setTranslations(Array(lyric.length).fill(''));
                        if (dataLesson?.id) {
                        if (skipEnsureOnThisPage) {
                            console.log('Skipping ensureOne after empty backend response because current path is Speaking');
                        } else if (isAdmin()) {
                            axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id, force: true }, { timeout: 60000 })
                                .then(() => {
                                    message.info('Đã gửi yêu cầu dịch; làm mới trang sau vài giây để xem bản dịch.');
                                    setTimeout(() => lesson(), 1200);
                                })
                                .catch(err => {
                                    console.error('ensureOne after empty backend response error', err);
                                    message.warning('Không thể gửi yêu cầu dịch tự động');
                                });
                        } else {
                            console.log('Skipping ensureOne after empty backend response: user not admin');
                        }
                    }
                }
            } else {
                console.warn('⚠️ Invalid or incomplete translation response structure:', result?.data);
                // Backend returned invalid/incomplete response — request server to ensure translations instead of calling external API
                console.warn('⚠️ Invalid or incomplete translation response; requesting server to ensure translations');
                setTranslations(Array(lyric.length).fill(''));
                if (dataLesson?.id) {
                    if (skipEnsureOnThisPage) {
                        console.log('Skipping ensureOne after invalid backend response because current path is Speaking');
                    } else if (isAdmin()) {
                        axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id }, { timeout: 60000 })
                            .then(() => {
                                message.info('Đã gửi yêu cầu dịch; làm mới trang sau vài giây để xem bản dịch.');
                                setTimeout(() => lesson(), 1200);
                            })
                            .catch(err => {
                                console.error('ensureOne after invalid backend response error', err);
                                message.warning('Không thể gửi yêu cầu dịch tự động');
                            });
                    } else {
                        console.log('Skipping ensureOne after invalid backend response: user not admin');
                    }
                }
            }
        } catch (e) {
            console.error('❌ Translation error:', e);
            console.error('Error details:', {
                message: e.message,
                response: e.response?.data,
                status: e.response?.status
            });
            setTranslations([]);
            
            if (e.code === 'ECONNABORTED') {
                message.error('Thời gian chờ quá lâu - Vui lòng thử lại');
            } else if (e.response?.status === 404) {
                message.error('API dịch không tìm thấy');
                } else {
                    message.error('Lỗi kết nối API dịch - yêu cầu server đảm nhận dịch');
                    setTranslations(Array(lyric.length).fill(''));
                    if (dataLesson?.id) {
                        if (skipEnsureOnThisPage) {
                            console.log('Skipping ensureOne after translation error because current path is Speaking');
                        } else if (isAdmin()) {
                            axios.post(`${process.env.REACT_APP_API_URL}/translation/ensureOne`, { lesson_id: dataLesson.id }, { timeout: 60000 })
                                .then(() => {
                                    message.info('Đã gửi yêu cầu dịch; làm mới trang sau vài giây để xem bản dịch.');
                                    setTimeout(() => lesson(), 1200);
                                })
                                .catch(err => {
                                    console.error('ensureOne after translation error', err);
                                    message.warning('Không thể gửi yêu cầu dịch tự động');
                                });
                        } else {
                            console.log('Skipping ensureOne after translation error: user not admin');
                        }
                    }
                }
        } finally {
            setTranslationLoading(false);
            console.log('fetchTranslations: Completed');
        }
    };

    // Try multiple key formats to locate per-track translations returned by backend
    const findTranslationsForTrack = (tRaw, storyName, expectedLength) => {
            if (!tRaw) return null;
            // If tRaw itself is a valid array, normalize to expectedLength
            if (Array.isArray(tRaw) && expectedLength) {
                const arr = tRaw.slice(0, expectedLength);
                while (arr.length < expectedLength) arr.push('');
                return arr;
            }
            const trackKey = toLowerCamelCase(storyName);
            const snakeFromCamel = trackKey.replace(/([A-Z])/g, '_$1').toLowerCase();
            const underscoredStory = String(storyName).replace(/\s+/g, '_').toLowerCase();
            const lowerKey = trackKey.toLowerCase();
            const variants = [trackKey, snakeFromCamel, underscoredStory, lowerKey, storyName];
            for (const k of variants) {
                if (!k) continue;
                if (Object.prototype.hasOwnProperty.call(tRaw, k)) {
                    const val = tRaw[k];
                    if (Array.isArray(val)) {
                        const arr = val.slice(0, expectedLength || val.length);
                        if (expectedLength) while (arr.length < expectedLength) arr.push('');
                        return arr;
                    }
                    if (typeof val === 'string') {
                        const arr = String(val).split('\n').map(s => s.trim());
                        const out = arr.slice(0, expectedLength || arr.length);
                        if (expectedLength) while (out.length < expectedLength) out.push('');
                        return out;
                    }
                }
            }
            return null;
    };

    // Remove leading timestamp patterns like [00:00.00] from a translation line
    const sanitizeTranslationLine = (s) => {
        if (!s && s !== '') return '';
        try {
            let str = String(s || '');
            // remove one or more leading [mm:ss.xx] or [m:ss.xx] or [hh:mm:ss] patterns
            str = str.replace(/^\s*(?:\[\d{1,2}:\d{2}(?:[:.]\d+)?\]\s*)+/, '');
            return str;
        } catch (e) {
            return String(s || '');
        }
    };

    const sanitizeTranslationsArray = (arr) => {
        if (!Array.isArray(arr)) return arr;
        return arr.map(sanitizeTranslationLine);
    };

    // Use shared lightweight IPA converter to ensure consistent spacing and rules
    const textToIPA = (text) => {
        try {
            return textToIPAPure(text);
        } catch (e) {
            console.warn('textToIPAPure error, fallback to empty', e && e.message);
            return '';
        }
    };

    const handleDurationUpdate = () => {
        const m = getMediaElem();
        if (!m) return;
        const d = m.duration;
        if (d && isFinite(d) && !isNaN(d) && d > 0) {
            setTotalDuration(d);
            return;
        }
        // Fallback: estimate from LRC timestamps
        const tsArr = timestamps[toLowerCamelCase(story)] || [];
        if (tsArr.length > 0) {
            const lastTs = tsArr[tsArr.length - 1] / 1000;
            setTotalDuration(lastTs + 10); // add ~10s buffer after last timestamp
        }
    };

    const handleLyric = (track) => {
        const startIndex = dataLesson[track]?.indexOf(']') + 1 || 0;
        const l = dataLesson[track]?.split('\n').map(el => el.slice(startIndex));
        setLyric(l);
    };

    const lesson = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/lesson/getBySlug/${slug}?_t=${Date.now()}`);
            let payload = response.data;
            if (Array.isArray(payload)) payload = payload[0];
            if (!payload) { setdataLesson({}); return; }

            const normalized = {
                id: payload.id || payload.lesson_id || null,
                title: payload.title || payload.name || payload.lesson_title || '',
                mainStory: payload.main_story || payload.mainStory || payload.main_story_text || payload.main || '',
                mainStoryAudio: payload.main_story_audio || payload.mainStoryAudio || payload.main_audio || payload.mainStoryUrl || payload.main_story_url || '',
                vocabulary: payload.vocabulary || payload.vocabulary_text || payload.vocabulary || '',
                vocabularyAudio: payload.vocabulary_audio || payload.vocabularyAudio || payload.vocabulary_audio_url || '',
                miniStory: payload.mini_story || payload.miniStory || payload.mini_story_text || '',
                miniStoryAudio: payload.mini_story_audio || payload.miniStoryAudio || payload.mini_story_audio_url || '',
                POV: payload.POV || payload.pov || payload.POV || payload.pov_text || '',
                POVAudio: payload.POV_audio || payload.POVAudio || payload.pov_audio || '',
                comment: payload.comment || payload.comments || payload.comment_text || '',
                commentAudio: payload.comment_audio || payload.commentAudio || '',
                translations: payload.translations || payload.translation_lines || payload.translation || payload.translations || [],
                _raw: payload,
            };

            // Normalize translations if backend returned JSON string, object with track keys, or newline text
            try {
                let t = normalized.translations;
                if (typeof t === 'string') {
                    try { t = JSON.parse(t); } catch (e) { t = String(t).split('\n').map(s => s.trim()); }
                }
                // If backend returned an object with per-track arrays, keep it as object
                if (Array.isArray(t) || (!t)) {
                    normalized.translations = Array.isArray(t) ? t : [];
                } else if (t && typeof t === 'object') {
                    normalized.translations = t; // object with track keys
                } else {
                    normalized.translations = [];
                }
            } catch (e) { normalized.translations = []; }

            // Initialize lyric/translations/ipa for current story so UI can render immediately
            try {
                const trackKey = toLowerCamelCase(story);
                const rawTrack = normalized[trackKey] || '';
                const startIndex = (rawTrack && rawTrack.indexOf(']') >= 0) ? rawTrack.indexOf(']') + 1 : 0;
                const l = rawTrack.split('\n').map(el => el.slice(startIndex));

                // If translations exist in lesson record, try finding per-track translations and set them immediately
                const tRaw = normalized.translations;
                const found = findTranslationsForTrack(tRaw, story, l.length);
                if (found) {
                    setTranslations(sanitizeTranslationsArray(found));
                    console.log('Using translations from lesson record for', story);
                } else {
                    setTranslations(Array.from({ length: l.length }, () => ''));
                }

                // Set lyric; IPA is derived later via memoization (computedIpaData)
                setLyric(l);
                // Finally set full lesson payload
                setdataLesson(normalized);
                // Try to fetch duration from the first available audio file immediately
                try {
                    const firstAudio = getFirstAudioUrl(normalized);
                    if (firstAudio) {
                        const proxied = proxyIfR2(firstAudio);
                        fetchFirstAudioDuration(proxied);
                    }
                } catch (e) { console.error('fetchFirstAudioDuration error', e); }
            } catch (e) {
                console.error('Init lyric/ipa/translations error', e);
                setdataLesson(normalized);
            }

        } catch (error) {
            console.error('Error fetching lesson by slug', slug, error);
            setdataLesson({});
        }
    };

    const userListening = async () => {
        const response = await getUserListening(cookies?.user?.token);
        setDataUserListening(response);
    };

    const onSubmit = async (values) => {
        if (values.text && values.text !== '') {
            await createUserListening({
                lesson_id: dataLesson.id, position: active,
                track: toLowerCamelCase(story), lyric: lyric[active], text: values.text,
            }, cookies?.user?.token);
            await userListening();
        }
        await stopMic();
    };

    const onPlaying = (ev) => {
        if (isSeekingRef.current) return;
        const media = getMediaElem();
        if (!media) return;
        const ct = media.currentTime;
        setcurrentTime(ct * 1000);
        // Update totalDuration if not yet set
        const d = media.duration;
        if (d && isFinite(d) && !isNaN(d) && d > 0) {
            setTotalDuration(prev => (prev !== d ? d : prev));
        }
        // Auto-advance active sentence in playAll mode
        if (playAll) {
            const ts = timestamps[toLowerCamelCase(story)] || [];
            if (ts.length > 0) {
                let newActive = active;
                for (let i = 0; i < ts.length; i++) {
                    if (ct * 1000 >= ts[i]) {
                        newActive = i;
                    } else {
                        break;
                    }
                }
                if (newActive !== active) {
                    setActive(newActive);
                }

                // Check end-of-current-active (use activeRef to reference the sentence that was active)
                const checkIdx = activeRef.current;
                if (typeof checkIdx === 'number' && checkIdx >= 0 && checkIdx < ts.length) {
                    const start = ts[checkIdx] / 1000;
                    const end = ts[checkIdx + 1] !== undefined ? ts[checkIdx + 1] / 1000 : start + 10;
                    const eps = 0.08;
                    if (ct >= end - eps) {
                        if (loopMode === 1) {
                            if (!sentenceRepeatedRef.current[checkIdx]) {
                                sentenceRepeatedRef.current[checkIdx] = true;
                                try { const m = getMediaElem(); if (m) m.currentTime = start; } catch (e) {}
                                return;
                            } else {
                                sentenceRepeatedRef.current[checkIdx] = false;
                            }
                        }
                    }
                }
            }
        }
    };

    const changeSpeedMode = (speed) => {
        setSpeedMode(speed);
        try { const m = getMediaElem(); if (m) m.playbackRate = speed; } catch (e) {}
    };

    const addTimestamps = async () => {
        const temp = {};
        const dataKeys = ['mainStory', 'miniStory', 'POV', 'comment', 'vocabulary'];
        for (const key of dataKeys) {
            if (dataLesson?.[key]) {
                const mapped = key === 'POV' ? 'pOV' : key;
                temp[mapped] = dataLesson[key].split('\n')
                    .map(el => convertTimeToMilliseconds(el.slice(1, el.indexOf(']'))));
            }
        }
        setTimestamps(temp);
    };

    const safePlay = async () => {
        try {
            const media = getMediaElem();
            if (!media) return;
            const src = media === videoElem.current ? getVideoSrc() : getAudioSrc();
            try {
                if (!media.src || media.src !== src) {
                    if (src) {
                        media.src = src;
                        try { media.load(); } catch (e) {}
                    }
                }
            } catch (e) {}
            const currentSrc = media.src || media.currentSrc;
            if (!currentSrc) return;
            await media.play();
        } catch (err) {
            try { setisPlaying(false); } catch (e) {}
        }
    };

    const play = async i => {
        try {
            const ts = timestamps[toLowerCamelCase(story)];
            const media = getMediaElem();
            if (!media) return;
            media.currentTime = ts[i] / 1000;
            await safePlay();
            const endTime = ts[i + 1] / 1000 ? ts[i + 1] / 1000 : i + 1 === ts.length ? media.duration : 0;
            setDuration(endTime - ts[i] / 1000);
        } catch (e) { console.log(e); }
    };

    const pause = () => {
        try {
            safePlay().then(() => {
                try {
                    const m = getMediaElem(); if (m) { m.pause(); }
                    setisPlaying(false);
                    setPlayAll(false); setDuration(0); setCount(1); clearTimeout(timeoutPlay);
                } catch (e) {}
            });
        } catch (e) {}
    };

    const PlayPause = async () => {
        if (!getAudioSrc() && !getVideoSrc()) { message.warning('Không có file âm thanh/ video cho bài này'); return; }
        if (isPlaying) {
            pause();
            setPlayAll(false);
        } else {
            // Resume playback from current position (don't reset after seek)
            try {
                const m = getMediaElem(); if (m) m.playbackRate = speedMode;
                // reset per-sentence repeat markers when starting playAll
                sentenceRepeatedRef.current = {};
                setPlayAll(true);
                await safePlay();
            } catch (e) { console.log(e); }
            setisPlaying(true);
        }
    };

    const handleChangeStory = (e) => { setStory(e); setActive(0); setisPlaying(false); };

    const clearAutoAdvanceLS = () => {
        if (autoAdvanceLSTimerRef.current) {
            clearTimeout(autoAdvanceLSTimerRef.current);
            autoAdvanceLSTimerRef.current = null;
        }
    };

    /** Play just a single sentence (seek to its timestamp, stop at next timestamp or +10s) */
    const playSentenceOnly = async (i) => {
        const tsArr = timestamps[toLowerCamelCase(story)] || [];
        if (!tsArr.length) { message.warning('Track này chưa có timestamp.'); return; }
        const media = getMediaElem();
        if (!media) { message.warning('Không có file âm thanh/video.'); return; }

        // Cancel any previous per-sentence playback interval
        if (playSentenceIntervalRef.current) {
            clearInterval(playSentenceIntervalRef.current);
            playSentenceIntervalRef.current = null;
        }
        // Stop full playback if running
        if (!media.paused) {
            media.pause();
            setisPlaying(false);
            setPlayAll(false);
        }

        setActive(i);
        setPlayingSentence(i);

        const start = tsArr[i] / 1000;
        const end = tsArr[i + 1] !== undefined ? tsArr[i + 1] / 1000 : start + 10;
        media.currentTime = start;
        media.playbackRate = speedModeRef.current;
        // If per-sentence video mode is enabled, start recorder for this sentence
        if (perSentenceVideoMode && perSentenceCameraStreamRef.current) {
            try {
                const vStream = perSentenceCameraStreamRef.current;
                const mimeTypes = ['video/webm;codecs=vp9,opus','video/webm;codecs=vp8,opus','video/webm','video/mp4'];
                let sel = '';
                for (const m of mimeTypes) { if (MediaRecorder.isTypeSupported(m)) { sel = m; break; } }
                const rec = new MediaRecorder(vStream, sel ? { mimeType: sel } : {});
                const chunks = [];
                rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
                rec.onstop = () => {
                    try {
                        const blob = new Blob(chunks, { type: sel || 'video/webm' });
                        const ext = sel && sel.includes('mp4') ? 'mp4' : 'webm';
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `video_sentence_${i}_${(dataLesson?.title||'lesson').replace(/\s+/g,'_')}.${ext}`;
                        a.click(); URL.revokeObjectURL(url);
                        message.success(`Đã lưu video câu ${i + 1}`);
                    } catch (e) { console.error('per-sentence video save error', e); }
                };
                try { rec.start(); perSentenceVideoRecorderRef.current = rec; } catch (e) { console.warn('start per-sentence recorder failed', e); }
            } catch (e) { console.warn('per-sentence recorder setup failed', e); }
        }
        try {
            // mark this sentence as not-yet-repeated for manual per-sentence play
            sentenceRepeatedRef.current[i] = false;
            await media.play();
        } catch (e) {
            setPlayingSentence(null);
            return;
        }

        // Poll to stop at end time; clear on unmount via ref
        playSentenceIntervalRef.current = setInterval(() => {
            if (!media || media.paused) {
                clearInterval(playSentenceIntervalRef.current);
                playSentenceIntervalRef.current = null;
                setPlayingSentence(null);
                return;
            }
            if (media.currentTime >= end) {
                // stop per-sentence recorder if active
                try {
                    if (perSentenceVideoMode && perSentenceVideoRecorderRef.current) {
                        try { perSentenceVideoRecorderRef.current.stop(); } catch (e) { console.warn('stop per-sentence recorder', e); }
                        perSentenceVideoRecorderRef.current = null;
                    }
                } catch (e) {}

                try { media.pause(); } catch (e) {}
                clearInterval(playSentenceIntervalRef.current);
                playSentenceIntervalRef.current = null;
                // Loop single sentence once if configured for manual per-sentence play
                if (loopMode === 1) {
                    if (!sentenceRepeatedRef.current[i]) {
                        sentenceRepeatedRef.current[i] = true;
                        try { media.currentTime = start; } catch (e) {}
                        return;
                    } else {
                        // already repeated once -> reset flag for future manual plays
                        sentenceRepeatedRef.current[i] = false;
                    }
                }
                setPlayingSentence(null);
            }
        }, 80);
    };

    /** ── Drill Mode helpers ──────────────────────────────────────────────── */
    const clearDrillInterval = () => {
        if (drillIntervalRef.current) {
            clearInterval(drillIntervalRef.current);
            drillIntervalRef.current = null;
        }
    };

    /** Start the stop-at-end interval using the already-set drillEndRef */
    const startDrillInterval = () => {
        clearDrillInterval();
        drillIntervalRef.current = setInterval(async () => {
            const media = getMediaElem();
            if (!media || media.paused) {
                setDrillIsPlaying(false);
                clearDrillInterval(); return;
            }
            if (media.currentTime >= drillEndRef.current) {
                // Stop per-sentence video recorder if active
                try {
                    if (perSentenceVideoMode && perSentenceVideoRecorderRef.current) {
                        try { perSentenceVideoRecorderRef.current.stop(); } catch (e) { console.warn('stop per-sentence recorder', e); }
                        perSentenceVideoRecorderRef.current = null;
                    }
                } catch (e) {}

                try { media.pause(); } catch (e) {}
                clearDrillInterval();

                const doneCount = drillRepeatCountRef.current + 1;
                if (doneCount < drillRepeatRef.current) {
                    // Repeat the same sentence
                    drillRepeatCountRef.current = doneCount;
                    const tsArr = timestamps[toLowerCamelCase(story)] || [];
                    const idx = drillIndexRef.current;
                    const start = tsArr[idx] !== undefined ? tsArr[idx] / 1000 : 0;
                    setTimeout(async () => {
                        if (!audioElem.current) return;
                        audioElem.current.currentTime = start;
                        audioElem.current.playbackRate = speedModeRef.current;
                        try { await audioElem.current.play(); } catch (e) { setDrillIsPlaying(false); return; }
                        setDrillIsPlaying(true);
                        startDrillInterval();
                    }, 350);
                } else {
                    // All repeats done
                    drillRepeatCountRef.current = 0;
                    setDrillIsPlaying(false);
                    if (drillAutoNextRef.current) {
                        const nextIdx = drillIndexRef.current + 1;
                        const tsArr = timestamps[toLowerCamelCase(story)] || [];
                        if (nextIdx < lyricRef.current?.length && tsArr[nextIdx] !== undefined) {
                            setTimeout(() => playDrillSentence(nextIdx), 500);
                        }
                    }
                }
            }
        }, 80);
    };

    /** Play the current drill sentence from the beginning and stop at the next timestamp */
    const playDrillSentence = async (idx) => {
        clearDrillInterval();
        const tsArr = timestamps[toLowerCamelCase(story)] || [];
        if (!tsArr.length || !audioElem.current) return;
        const media = getMediaElem();
        if (!media) return;
        drillIndexRef.current = idx;
        setDrillIndex(idx);
        setDrillAnswer('');
        setDrillAnswerResult(null);
        drillRepeatCountRef.current = 0;
        const start = tsArr[idx] / 1000;
        const end = tsArr[idx + 1] !== undefined ? tsArr[idx + 1] / 1000 : start + 10;
        drillEndRef.current = end;
        media.currentTime = start;
        media.playbackRate = speedModeRef.current;
        // If per-sentence video mode is enabled, start a short MediaRecorder for the camera stream
        if (perSentenceVideoMode && perSentenceCameraStreamRef.current) {
            try {
                const vStream = perSentenceCameraStreamRef.current;
                const mimeTypes = ['video/webm;codecs=vp9,opus','video/webm;codecs=vp8,opus','video/webm','video/mp4'];
                let sel = '';
                for (const m of mimeTypes) { if (MediaRecorder.isTypeSupported(m)) { sel = m; break; } }
                const rec = new MediaRecorder(vStream, sel ? { mimeType: sel } : {});
                const chunks = [];
                rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
                rec.onstop = () => {
                    try {
                        const blob = new Blob(chunks, { type: sel || 'video/webm' });
                        const ext = sel && sel.includes('mp4') ? 'mp4' : 'webm';
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `video_sentence_${idx}_${(dataLesson?.title||'lesson').replace(/\s+/g,'_')}.${ext}`;
                        a.click(); URL.revokeObjectURL(url);
                        message.success(`Đã lưu video câu ${idx + 1}`);
                    } catch (e) { console.error('per-sentence video save error', e); }
                };
                try { rec.start(); perSentenceVideoRecorderRef.current = rec; } catch (e) { console.warn('start per-sentence recorder failed', e); }
            } catch (e) { console.warn('per-sentence recorder setup failed', e); }
        }
        try { await media.play(); } catch (e) { return; }
        setDrillIsPlaying(true);
        startDrillInterval();
    };

    const openDrillMode = () => {
        // Stop any ongoing playback
        if (!audioElem.current?.paused) { audioElem.current.pause(); setisPlaying(false); setPlayAll(false); }
        setDrillIndex(0);
        setShowDrillMode(true);
        // Auto-play first sentence after brief delay
        setTimeout(() => playDrillSentence(0), 200);
    };

    const closeDrillMode = () => {
        clearDrillInterval();
        if (!audioElem.current?.paused) { audioElem.current.pause(); }
        setDrillIsPlaying(false);
        setShowDrillMode(false);
        setDrillAnswer('');
        setDrillAnswerResult(null);
    };

    const checkDrillAnswer = () => {
        const correct = lyric?.[drillIndex] || '';
        if (!drillAnswer.trim() || !correct) return;
        const clean = s => s.replace(/[!@#$%^&*(),.?"":{}|<>""''`';:]/g, '').toLowerCase().trim();
        const correctWords = correct.split(/\s+/).filter(Boolean);
        const answerWords = drillAnswer.trim().split(/\s+/).filter(Boolean);
        const answerSet = new Set(answerWords.map(clean));
        let matches = 0;
        const words = correctWords.map(w => {
            const matched = answerSet.has(clean(w));
            if (matched) matches++;
            return { word: w, matched };
        });
        const point = Math.round((matches / (correctWords.length || 1)) * 100);
        setDrillAnswerResult({ point, words });
    };

    /**
     * Timer-based lyric auto-advance (used when no audio is available).
     * Each sentence stays on screen for (wordCount / (2.3 * speed)) seconds.
     */
    const WORDS_PER_SEC_LS = 2.3;
    const MIN_MS_LS = 1200;
    const scheduleAutoAdvanceLS = (startIdx) => {
        clearAutoAdvanceLS();
        const advanceTo = (idx) => {
            const lines = lyricRef.current;
            if (!lines || idx >= lines.length) return;
            setActive(idx);
            const wordCount = (lines[idx] || '').split(/\s+/).filter(Boolean).length || 1;
            const speed = speedModeRef.current;
            const ms = Math.max(MIN_MS_LS, (wordCount / (WORDS_PER_SEC_LS * speed)) * 1000);
            autoAdvanceLSTimerRef.current = setTimeout(() => {
                if (!shadowReadingRef.current) return;
                advanceTo(idx + 1);
            }, ms);
        };
        advanceTo(startIdx);
    };

    /**
     * "Luyện nói": ghi âm continuous xuyên suốt, lyric tự chạy theo timer (speedMode).
     * Không phát audio. Khi Dừng → batch score tất cả câu.
     */
    const clickMic = async () => {
        try { SpeechRecognition.getRecognition().abort(); } catch (e) {}
        try { SpeechRecognition.resetTranscript(); } catch (e) {}
        fullTranscriptRef.current = '';
        shadowReadingRef.current = true;
        setScoreResult(null); // clear previous results
        // Use timer-based lyric advance (same as Speaking.js) — no audio playback
        setActive(0);
        scheduleAutoAdvanceLS(0);
        await SpeechRecognition.startListening({ language: 'en-US', continuous: true });
    };

    const stopMic = async () => {
        shadowReadingRef.current = false;
        clearAutoAdvanceLS();
        await SpeechRecognition.stopListening();
        // batchScoreShadow triggered from listening useEffect
    };

    const scoreTextLS = (sentenceWords, resultText) => {
        const resultWords = resultText
            .replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '')
            .toLowerCase().split(/\s+/);
        let matches = 0;
        sentenceWords.forEach(w => {
            const clean = w.replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '').toLowerCase();
            if (resultWords.includes(clean)) matches++;
        });
        return Math.round((matches / (sentenceWords.length || 1)) * 100);
    };

    const batchScoreShadow = async (fullText) => {
        if (!fullText?.trim() || !lyric?.length) return;
        setIsBatchScoring(true);
        try {
            // Align transcript to each lyric line (greedy sequence alignment)
            const aligned = alignTranscript(fullText, lyric);

            const lines = lyric.map((line, pos) => {
                const rawPoint = (aligned[pos] && aligned[pos].point) || 0;
                let p = Math.round(rawPoint);
                if (p < 25) p = 0;
                return {
                    lyric: line,
                    pos,
                    point: p,
                    words: aligned[pos]?.words || [],
                    spokenLine: aligned[pos]?.spokenLine || [],
                };
            });

            const avgPoint = Math.round(
                lines.reduce((s, l) => s + l.point, 0) / (lines.length || 1)
            );

            // POST to server — send the aligned chunk for each sentence
            const posts = lyric.map((line, pos) =>
                createUserListening({
                    lesson_id: dataLesson.id, position: pos,
                    track: toLowerCamelCase(story),
                    lyric: line || '',
                    text: aligned[pos].chunk.join(' ') || fullText,
                }, cookies?.user?.token)
            );
            await Promise.all(posts);
            await userListening();

            // Show results modal (skip for specific lesson slug)
            setScoreResult({ avgPoint, spokenText: fullText, lines, totalLines: lyric.length });
            if (slug !== 'bai-01-day-of-the-dead') setShowScoreModal(true);
        } catch (e) {
            console.error('Batch score error', e);
            message.error('Có lỗi khi chấm điểm.');
        } finally {
            setIsBatchScoring(false);
            try { SpeechRecognition.resetTranscript(); } catch (e) {}
        }
    };

    const handleCheckShadow = async () => {
        const spoken = (fullTranscriptRef.current || '').trim();
        if (!spoken) { message.warning('Không có transcript để chấm'); return; }
        try {
            setIsBatchScoring(true);
            await batchScoreShadow(spoken);
        } catch (e) {
            console.error('Error scoring manual transcript', e);
            message.error('Không thể chấm transcript. Thử lại.');
        } finally {
            setIsBatchScoring(false);
        }
    };

    const formatTime = (s) => {
        if (!s || isNaN(s) || !isFinite(s)) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec < 10 ? '0' : ''}${sec}`;
    };

    const downloadAudio = () => {
        const src = getAudioSrc();
        if (!src) { message.error('Không tìm thấy file âm thanh!'); return; }
        const link = document.createElement('a');
        link.href = src;
        link.download = `${dataLesson?.title || 'audio'}_${story.replace(/\s/g, '_')}.mp3`;
        link.target = '_blank';
        link.click();
    };

    // Drill mode audio recording with word highlighting
    const startDrillAudioRecording = async () => {
        try {
            // Reset to beginning
            setDrillIndex(0);
            setDrillAnswer('');
            setDrillAnswerResult(null);
            resetDrillTranscript();

            // Start speech recognition first so initial words are captured
            try { await SpeechRecognition.startListening({ language: 'en-US', continuous: true }); } catch (e) { console.warn('SpeechRecognition start failed', e); }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeTypes = ['audio/webm', 'audio/mp4', 'audio/ogg'];
            let selectedMime = '';
            for (const mime of mimeTypes) {
                if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; }
            }
            const recorderOptions = selectedMime ? { mimeType: selectedMime } : {};
            const recorder = new MediaRecorder(stream, recorderOptions);
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                const ext = selectedMime.includes('mp4') ? 'm4a' : selectedMime.includes('ogg') ? 'ogg' : 'webm';
                const blob = new Blob(chunks, { type: selectedMime || 'audio/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `recording_audio_${dataLesson?.title || 'lesson'}.${ext}`;
                a.click(); URL.revokeObjectURL(url);
                message.success('Đã lưu bản ghi âm!');
                stream.getTracks().forEach(t => t.stop());
            };
            recorder.start();
            setDrillMediaRecorder(recorder);
            setDrillRecording(true);
            // SpeechRecognition already started; mark listening state
            setDrillListening(true);
            // Play from beginning
            setTimeout(() => playDrillSentence(0), 200);
            message.success('Đang ghi âm và theo dõi lời nói...');
        } catch (err) {
            console.error('Audio recording error:', err);
            if (err.name === 'NotAllowedError') {
                message.error('Bạn đã từ chối quyền truy cập microphone.');
            } else {
                message.error('Không thể bắt đầu ghi âm: ' + (err.message || err.name));
            }
        }
    };

    const stopDrillAudioRecording = async () => {
        if (drillMediaRecorder && drillRecording) {
            drillMediaRecorder.stop();
            setDrillRecording(false);
            setDrillMediaRecorder(null);
            // Stop speech recognition
            try { await SpeechRecognition.stopListening(); } catch (e) { try { SpeechRecognition.abortListening && SpeechRecognition.abortListening(); } catch(_){} }
            setDrillListening(false);

            // Wait briefly for recognition to finalize
            await new Promise(r => setTimeout(r, 300));

            const spoken = (drillRecordingTranscript || transcript || fullTranscriptRef.current || '').trim();
            if (spoken) {
                // If user is logged in, batch-score and save results. Otherwise show recording modal only.
                if (cookies?.user) {
                    try {
                        setIsBatchScoring(true);
                        await batchScoreShadow(spoken);
                    } catch (e) {
                        console.error('Error scoring recording:', e);
                        message.error('Không thể chấm điểm bản ghi âm.');
                    } finally {
                        setIsBatchScoring(false);
                    }
                } else {
                    // guest: show transcript modal without saving
                    setShowDrillRecordingModal(true);
                }
            }
            else {
                message.warning('Không có transcript để chấm. Vui lòng bật nhận dạng giọng nói khi ghi âm.');
            }
        }
    };

    // Drill mode video recording - doesn't affect audio playback
    const startDrillVideoRecording = async () => {
        try {
            // Reset transcript
            setDrillRecordingTranscript('');
            
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: true
            });
            drillCameraStreamRef.current = stream;
            if (drillCameraPreviewRef.current) {
                drillCameraPreviewRef.current.srcObject = stream;
                // Ensure video plays
                try {
                    await drillCameraPreviewRef.current.play();
                } catch (playErr) {
                    console.log('Video autoplay handled by browser');
                }
            }
            const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
            let selectedMime = '';
            for (const mime of mimeTypes) {
                if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; }
            }
            const recorderOptions = selectedMime ? { mimeType: selectedMime } : {};
            const recorder = new MediaRecorder(stream, recorderOptions);
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                const ext = selectedMime.includes('mp4') ? 'mp4' : 'webm';
                const blob = new Blob(chunks, { type: selectedMime || 'video/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `recording_video_${dataLesson?.title || 'lesson'}.${ext}`;
                a.click(); URL.revokeObjectURL(url);
                message.success('Đã lưu bản ghi hình!');
                stream.getTracks().forEach(t => t.stop());
                if (drillCameraPreviewRef.current) drillCameraPreviewRef.current.srcObject = null;
                drillCameraStreamRef.current = null;
            };
            recorder.start();
            setDrillMediaRecorder(recorder);
            setDrillVideoRecording(true);
            
            // Start speech recognition for word highlighting
            if (browserSupportsSpeechRecognition) {
                try {
                    await SpeechRecognition.startListening({ language: 'en-US', continuous: true });
                    setDrillListening(true);
                    console.log('Speech recognition started successfully');
                } catch (speechErr) {
                    console.error('Speech recognition error:', speechErr);
                    message.warning('Ghi hình đã bắt đầu nhưng không thể bật nhận dạng giọng nói');
                }
            } else {
                console.warn('Browser does not support speech recognition');
                message.warning('Trình duyệt không hỗ trợ nhận dạng giọng nói - chỉ ghi hình và âm thanh');
            }
            
            message.success('Đang ghi hình và thu âm!');
        } catch (err) {
            console.error('Video recording error:', err);
            if (err.name === 'NotAllowedError') {
                message.error('Bạn đã từ chối quyền truy cập camera/microphone.');
            } else if (err.name === 'NotFoundError') {
                message.error('Không tìm thấy camera.');
            } else {
                message.error('Không thể bắt đầu ghi hình: ' + (err.message || err.name));
            }
        }
    };

    

    const togglePerSentenceVideoMode = async (enable) => {
        if (enable) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true });
                perSentenceCameraStreamRef.current = stream;
                if (drillCameraPreviewRef.current) {
                    drillCameraPreviewRef.current.srcObject = stream;
                    try { await drillCameraPreviewRef.current.play(); } catch (e) { /* ignore */ }
                }
                setPerSentenceVideoMode(true);
                message.success('Bật chế độ ghi hình từng câu');
            } catch (e) {
                console.error('Could not get camera for per-sentence mode', e);
                message.error('Không thể bật camera');
                setPerSentenceVideoMode(false);
            }
        } else {
            try {
                if (perSentenceCameraStreamRef.current) {
                    perSentenceCameraStreamRef.current.getTracks().forEach(t => t.stop());
                    perSentenceCameraStreamRef.current = null;
                }
            } catch (e) {}
            setPerSentenceVideoMode(false);
            message.info('Tắt chế độ ghi hình từng câu');
        }
    };

    const stopDrillVideoRecording = () => {
        if (drillMediaRecorder && drillVideoRecording) {
            message.info('Đang dừng và lưu bản ghi hình...');
            drillMediaRecorder.stop();
            setDrillVideoRecording(false);
            setDrillMediaRecorder(null);
            // Stop speech recognition
            SpeechRecognition.stopListening();
            setDrillListening(false);
            // Show recording summary modal
            setTimeout(() => {
                setShowDrillRecordingModal(true);
            }, 500);
        }
    };

    // Close drill camera without recording
    const closeDrillCamera = () => {
        if (drillCameraStreamRef.current) {
            drillCameraStreamRef.current.getTracks().forEach(t => t.stop());
            if (drillCameraPreviewRef.current) drillCameraPreviewRef.current.srcObject = null;
            drillCameraStreamRef.current = null;
        }
        setDrillVideoRecording(false);
        setDrillMediaRecorder(null);
        message.info('Đã đóng camera.');
    };

    const ts = timestamps[toLowerCamelCase(story)] || [];
    const audioSrc = getAudioSrc();

    useEffect(() => {
        try {
            console.debug('Listening audioSrc:', audioSrc);
            if (audioElem.current) {
                // Ensure crossOrigin set before assigning/using src for CORS requests
                try { audioElem.current.crossOrigin = 'anonymous'; } catch (e) {}
            }
        } catch (e) { console.error('audioSrc debug error', e); }
    }, [audioSrc]);

    // Prime IPA computation for first visible lines and nearby lines
    useEffect(() => {
        if (!showIPA) return;
        const initial = [];
        for (let i = 0; i < Math.min(5, (lyric || []).length); i++) initial.push(i);
        // also compute a sliding window around active
        const window = [];
        for (let j = Math.max(0, active - 2); j <= Math.min((lyric || []).length - 1, active + 2); j++) window.push(j);
        scheduleComputeIpa([...new Set([...initial, ...window])]);
    }, [lyric, active, showIPA]);

    // Compute actual practice counts/hours for this lesson from userListening data
    const actualSessions = (dataUserListening || []).filter(item => +item.lesson_id === +dataLesson?.id).length;
    const lessonDurationHours = (totalDuration || 0) / 3600; // seconds -> hours
    const actualHours = Math.round((lessonDurationHours * actualSessions) * 100) / 100;
    const daysPercent = goalDays > 0 ? Math.min(100, Math.round((actualSessions / goalDays) * 100)) : 0;
    const hoursPercent = goalHours > 0 ? Math.min(100, Math.round((actualHours / goalHours) * 100)) : 0;

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0c1929] to-[#162d50]">
            {/* Hidden Audio */}
            {getVideoSrc() && (
                <video src={getVideoSrc()} ref={videoElem} crossOrigin="anonymous" onError={(e)=>{ console.error('Video element load/play error', e); message.error('Không thể tải video'); }} onTimeUpdate={onPlaying}
                    preload="metadata"
                    onLoadedMetadata={handleDurationUpdate}
                    onDurationChange={handleDurationUpdate}
                    onCanPlay={handleDurationUpdate}
                    onEnded={() => {
                        const media = getMediaElem();
                        if (loopMode === 2 && playAll) {
                            try { media.currentTime = 0; media.play(); setisPlaying(true); } catch (e) { setisPlaying(false); setPlayAll(false); }
                        } else {
                            setisPlaying(false); setPlayAll(false);
                        }
                    }}
                    style={{ width: '100%', maxHeight: 420, borderRadius: 8 }}
                    controls
                    />
            )}

            {/* Hidden Audio (fallback when no video) */}
            {audioSrc && (
                <audio src={audioSrc} ref={audioElem} crossOrigin="anonymous" onError={(e)=>{ console.error('Audio element load/play error', e); message.error('Không thể tải file âm thanh'); }} onTimeUpdate={onPlaying}
                    preload="metadata"
                    onLoadedMetadata={handleDurationUpdate}
                    onDurationChange={handleDurationUpdate}
                    onCanPlay={handleDurationUpdate}
                    onEnded={() => {
                        const media = getMediaElem();
                        if (loopMode === 2 && playAll) {
                            try { media.currentTime = 0; media.play(); setisPlaying(true); } catch (e) { setisPlaying(false); setPlayAll(false); }
                        } else {
                            setisPlaying(false); setPlayAll(false);
                        }
                    }} />
            )}

            {/* Header Bar */}
            <div className="bg-[#0a1628]/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-20">
                <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Breadcrumb
                        className="[&_.ant-breadcrumb-link]:text-blue-300 [&_.ant-breadcrumb-separator]:text-blue-400 [&_a]:text-blue-300 [&_a:hover]:text-white"
                        items={[
                            { title: <Link to="/"><HomeOutlined /> Trang chủ</Link> },
                            { title: <Link to="/topic">Topic</Link> },
                            { title: <Link to={"/lesson/" + dataLesson?.course_slug}>{dataLesson?.course_title}</Link> },
                            { title: <span className="text-white">{dataLesson?.title}</span> },
                        ]}
                    />
                </div>
            </div>

            <div className="max-w-screen-xl mx-auto px-4 py-6 pb-[140px]">

                

                {/* ── Drill Mode: inline panel ───────────────────────────────── */}
                {showDrillMode && (() => {
                    const validLyric = lyric?.filter(l => l?.trim()) || [];
                    const validIdx = validLyric.indexOf(lyric?.[drillIndex] || '');
                    const pct = validLyric.length ? Math.round(((validIdx + 1) / validLyric.length) * 100) : 0;
                    return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Main Content - 2 columns */}
                        <div className="lg:col-span-2 space-y-4">
                            {/* Header row */}
                            <div className="w-full flex items-center justify-between">
                                <button
                                    onClick={closeDrillMode}
                                    className="flex items-center gap-2 text-blue-300 hover:text-white transition-colors text-sm font-medium"
                                >
                                    <StepBackwardOutlined /> Quay lại
                                </button>
                                <p className="text-gray-400 text-sm tracking-wide">
                                    📖 <span className="text-blue-300">{story}</span>
                                    &nbsp;·&nbsp; Câu <span className="text-white font-bold">{validIdx + 1}</span> / {validLyric.length}
                                </p>
                                <button
                                    onClick={() => setShowDrillSettings(v => !v)}
                                    className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors lg:hidden"
                                >
                                    <SettingOutlined /> Cài đặt
                                </button>
                            </div>

                        {/* Progress bar using Ant Design Progress */}
                        <div className="w-full mb-4">
                            <Progress
                                percent={pct}
                                showInfo={false}
                                strokeColor="#22d3ee"
                                trailColor="rgba(255,255,255,0.08)"
                                strokeLinecap="round"
                                className="[&_.ant-progress-inner]:!bg-white/5"
                            />
                        </div>

                        {/* Per-sentence audio progress bar */}
                        {(() => {
                            const tsArr = timestamps[toLowerCamelCase(story)] || [];
                            const sentenceStart = tsArr[drillIndex] !== undefined ? tsArr[drillIndex] / 1000 : 0;
                            const sentenceEnd = tsArr[drillIndex + 1] !== undefined
                                ? tsArr[drillIndex + 1] / 1000
                                : sentenceStart + 10;
                            const sentenceDuration = Math.max(sentenceEnd - sentenceStart, 0.001);
                            const elapsed = Math.min(Math.max((currentTime / 1000) - sentenceStart, 0), sentenceDuration);
                            const sentencePct = (elapsed / sentenceDuration) * 100;
                            return (
                                <div className="w-full mb-6">
                                    <div className="flex items-center gap-3">
                                        <span className="text-gray-500 text-xs font-mono min-w-[32px] text-right">
                                            {formatTime(elapsed)}
                                        </span>
                                        <input
                                            type="range"
                                            min={0}
                                            max={sentenceDuration}
                                            step={0.05}
                                            value={elapsed}
                                            onChange={(e) => {
                                                const val = sentenceStart + parseFloat(e.target.value);
                                                if (audioElem.current && isFinite(val)) {
                                                    audioElem.current.currentTime = val;
                                                    setcurrentTime(val * 1000);
                                                }
                                            }}
                                            className="flex-1 h-2 rounded-full appearance-none cursor-pointer"
                                            style={{
                                                background: `linear-gradient(to right, #22d3ee ${sentencePct}%, rgba(255,255,255,0.08) ${sentencePct}%)`,
                                                accentColor: '#22d3ee',
                                            }}
                                        />
                                        <span className="text-gray-500 text-xs font-mono min-w-[32px]">
                                            {formatTime(sentenceDuration)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Camera Preview - shown when recording */}
                        {drillVideoRecording && (
                            <div className="mb-6 flex justify-center">
                                <div className="relative">
                                    <video
                                        ref={drillCameraPreviewRef}
                                        autoPlay
                                        muted
                                        playsInline
                                        className="rounded-2xl border-2 border-red-500 shadow-2xl shadow-red-500/30"
                                        style={{ width: '400px', maxWidth: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
                                    />
                                    {/* Recording indicator */}
                                    <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse flex items-center gap-2">
                                        <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                                        Đang ghi hình
                                    </div>
                                    {/* Mic indicator */}
                                    <div className="absolute top-3 left-3 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2">
                                        <span className="text-sm">🎤</span>
                                        Mic {drillListening ? 'ON' : 'OFF'}
                                    </div>
                                    {/* Transcript preview at bottom */}
                                    {drillRecordingTranscript && (
                                        <div className="absolute bottom-3 left-3 right-3 bg-black/70 text-white px-3 py-2 rounded-lg text-xs max-h-20 overflow-y-auto">
                                            <p className="text-gray-300 text-[10px] mb-1">Transcript:</p>
                                            {drillRecordingTranscript}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Controls: Prev | Play/Pause | Next | Camera */}
                        <div className="flex items-center justify-center gap-6 mb-6">
                            <button
                                onClick={() => drillIndex > 0 && playDrillSentence(drillIndex - 1)}
                                disabled={drillIndex === 0}
                                className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                title="Câu trước"
                            >
                                <StepBackwardOutlined className="text-2xl" />
                            </button>

                            <button
                                onClick={async () => {
                                    if (drillIsPlaying) {
                                        clearDrillInterval();
                                        audioElem.current?.pause();
                                        setDrillIsPlaying(false);
                                    } else {
                                        if (!audioElem.current) return;
                                        const tsArr = timestamps[toLowerCamelCase(story)] || [];
                                        const sentenceStart = tsArr[drillIndex] !== undefined ? tsArr[drillIndex] / 1000 : 0;
                                        const sentenceEnd = tsArr[drillIndex + 1] !== undefined
                                            ? tsArr[drillIndex + 1] / 1000
                                            : sentenceStart + 10;
                                        // If audio ended or is past the sentence end, restart from sentence start
                                        const ct = audioElem.current.currentTime;
                                        if (audioElem.current.ended || ct >= sentenceEnd || ct < sentenceStart) {
                                            audioElem.current.currentTime = sentenceStart;
                                            drillEndRef.current = sentenceEnd;
                                        }
                                        audioElem.current.playbackRate = speedModeRef.current;
                                        try { await audioElem.current.play(); } catch (e) { return; }
                                        setDrillIsPlaying(true);
                                        startDrillInterval();
                                    }
                                }}
                                className={`w-20 h-20 rounded-full text-white flex items-center justify-center shadow-xl transition-all ${
                                    drillIsPlaying
                                        ? 'bg-cyan-400 shadow-cyan-400/40 scale-95 animate-pulse'
                                        : 'bg-cyan-500 hover:bg-cyan-600 shadow-cyan-500/30'
                                }`}
                                title={drillIsPlaying ? 'Dừng' : 'Phát câu này'}
                            >
                                {drillIsPlaying ? <PauseCircleFilled className="text-4xl" /> : <PlayCircleFilled className="text-4xl" />}
                            </button>

                            <button
                                onClick={() => drillIndex < (lyric?.length || 1) - 1 && playDrillSentence(drillIndex + 1)}
                                disabled={drillIndex >= (lyric?.length || 1) - 1}
                                className="w-14 h-14 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                title="Câu tiếp"
                            >
                                <StepForwardOutlined className="text-2xl" />
                            </button>
                        </div>

                        {/* ── Answer input ─────────────────────────────────────── */}
                        <div className="w-full bg-[#1a2d4a] rounded-2xl p-5 border border-white/10 mb-4">


                            <p className="text-gray-400 text-xs uppercase tracking-wider mb-3 flex items-center justify-between">
                                <span>Gõ lại những gì bạn nghe được</span>
                                {drillListening && <span className="text-red-400 text-xs animate-pulse">● Đang nghe...</span>}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={async () => {
                                        if (drillVideoRecording) {
                                            // Video recording mode - do nothing on mic button
                                            message.info('Vui lòng dùng nút "Dừng ghi hình" ở sidebar');
                                            return;
                                        }
                                        // Normal mic mode
                                        if (drillListening) {
                                            SpeechRecognition.stopListening();
                                            setDrillListening(false);
                                        } else {
                                            if (!browserSupportsSpeechRecognition) {
                                                message.error('Trình duyệt không hỗ trợ nhận diện giọng nói');
                                                return;
                                            }
                                            resetDrillTranscript();
                                            try {
                                                await SpeechRecognition.startListening({ language: 'en-US', continuous: false });
                                                setDrillListening(true);
                                            } catch (err) {
                                                console.error('Speech recognition error:', err);
                                                message.error('Không thể khởi động mic');
                                            }
                                        }
                                    }}
                                    className={`w-12 h-12 flex-shrink-0 rounded-xl transition-all flex items-center justify-center ${
                                        drillVideoRecording ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' :
                                        drillListening
                                            ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
                                            : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                    }`}
                                    title={drillVideoRecording ? 'Chế độ ghi hình' : (drillListening ? 'Dừng lắng nghe' : 'Nói để nhập')}
                                >
                                    {drillVideoRecording ? <VideoCameraOutlined className="text-lg" /> : <AudioFilled className="text-lg" />}
                                </button>
                                <input
                                    type="text"
                                    value={drillAnswer}
                                    onChange={e => { setDrillAnswer(e.target.value); setDrillAnswerResult(null); }}
                                    onKeyDown={e => e.key === 'Enter' && checkDrillAnswer()}
                                    placeholder="Nhập câu trả lời hoặc bấm mic để nói..."
                                    className="flex-1 bg-[#0f1e35] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-cyan-500/50"
                                />
                                <button
                                    onClick={checkDrillAnswer}
                                    disabled={!drillAnswer.trim()}
                                    className="px-4 py-2.5 rounded-xl bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 flex-shrink-0"
                                >
                                    <CheckOutlined /> Kiểm tra
                                </button>
                            </div>

                            {/* Result display */}
                            {drillAnswerResult && (
                                <div className="mt-4">
                                    <div className="flex items-center gap-4 mb-3">
                                        <Progress
                                            type="circle" size={56}
                                            percent={drillAnswerResult.point}
                                            strokeColor={drillAnswerResult.point >= 80 ? '#22c55e' : drillAnswerResult.point >= 50 ? '#f59e0b' : '#ef4444'}
                                            trailColor="rgba(255,255,255,0.1)"
                                            format={p => <span className="text-white font-bold text-sm">{p}%</span>}
                                        />
                                        <div>
                                            <p className={`font-bold text-lg ${drillAnswerResult.point >= 80 ? 'text-green-400' : drillAnswerResult.point >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                                                {drillAnswerResult.point >= 80 ? '🎉 Xuất sắc!' : drillAnswerResult.point >= 50 ? '👍 Khá tốt!' : '💪 Cần luyện thêm!'}
                                            </p>
                                            <p className="text-gray-400 text-xs">Độ chính xác</p>
                                        </div>
                                    </div>
                                    <div className="bg-[#0f1e35] rounded-xl px-4 py-3 flex flex-wrap gap-x-1.5 gap-y-1">
                                        {drillAnswerResult.words.map(({ word, matched }, i) => (
                                            <span key={i} className={`font-semibold text-sm ${matched ? 'text-green-400' : 'text-red-400'}`}>
                                                {word}
                                            </span>
                                        ))}
                                    </div>
                                    
                                    {/* IPA */}
                                    {showIPA && (getIpaForIndex(drillIndex) !== null) && (
                                        <div className="mt-3 bg-[#0f1e35] rounded-xl px-4 py-2 border border-cyan-500/20">
                                            <p className="text-cyan-400 text-xs mb-1 uppercase tracking-wider">Phiên âm IPA:</p>
                                            <p className="text-cyan-300 font-mono text-sm">{getIpaForIndex(drillIndex) || <span className="opacity-30">—</span>}</p>
                                        </div>
                                    )}
                                    
                                    {/* Translation */}
                                    {showTranslation && translations[drillIndex] && (
                                        <div className="mt-2 bg-[#0f1e35] rounded-xl px-4 py-2 border border-yellow-500/20">
                                            <p className="text-yellow-400 text-xs mb-1 uppercase tracking-wider">Bản dịch:</p>
                                            <p className="text-yellow-300 italic text-sm">{translations[drillIndex]}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar: Settings & Camera - 1 column */}
                    <div className={`lg:col-span-1 space-y-4 ${showDrillSettings ? '' : 'hidden lg:block'}`}>
                        {/* Settings Panel */}
                        <div className="bg-[#1a2d4a] rounded-2xl p-5 border border-white/10 sticky top-20">
                            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Cài đặt</h3>
                            <div className="space-y-4">
                                {/* Speed */}
                                <div>
                                    <p className="text-gray-400 text-xs mb-2 uppercase tracking-wider flex items-center gap-1">
                                        ⚡ Tốc độ phát
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {speeds.map(s => (
                                            <button key={s} onClick={() => changeSpeedMode(s)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                    speedMode === s
                                                        ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                                                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                                }`}>
                                                {s}x
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="border-t border-white/10" />

                                {/* Repeat count */}
                                <div>
                                    <p className="text-gray-400 text-xs mb-2 uppercase tracking-wider flex items-center gap-1">
                                        🔁 Lặp lại mỗi câu: <span className="text-white font-bold ml-1">{drillRepeat} lần</span>
                                    </p>
                                    <Slider
                                        min={1} max={10} step={1} value={drillRepeat}
                                        onChange={v => setDrillRepeat(v)}
                                        className="[&_.ant-slider-rail]:!bg-white/10 [&_.ant-slider-track]:!bg-cyan-500 [&_.ant-slider-handle]:!border-cyan-500"
                                    />
                                </div>

                                <div className="border-t border-white/10" />

                                {/* Auto-next */}
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400 text-xs uppercase tracking-wider flex items-center gap-1">
                                        ⏭ Tự động sang câu tiếp
                                    </span>
                                    <Switch
                                        size="small"
                                        checked={drillAutoNext}
                                        onChange={v => setDrillAutoNext(v)}
                                        className={`${drillAutoNext ? '!bg-cyan-500' : '!bg-gray-600'}`}
                                    />
                                </div>

                            </div>
                        </div>

                        {/* Tools panel: replace recording controls with navigation buttons */}
                        <div className="bg-[#1a2d4a] rounded-2xl p-5 border border-white/10">
                            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Công cụ</h3>
                            <div className="space-y-3">
                                <button
                                    onClick={() => navigate(`/lesson/${dataLesson?.course_slug}`)}
                                    className="flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-white/5 text-white hover:bg-white/10"
                                >
                                    <HomeOutlined className="text-lg" /> Bài học
                                </button>

                                <button
                                    onClick={() => navigate(`/lesson/speaking/${slug}`, { state: { index } })}
                                    className="flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-cyan-500 text-white hover:bg-cyan-600"
                                >
                                    <SoundOutlined className="text-lg" /> Luyện nói
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                    );
                })()}

                {/* ── Normal mode ───────────────────────────────────────────── */}
                {!showDrillMode && (
                <>

                {/* Track Selector */}
                <div className="flex justify-center mb-6">
                    <div className="bg-[#1a2d4a] rounded-xl p-1 inline-flex gap-1 flex-wrap justify-center">
                        {options.map(opt => (
                            <button
                                key={opt}
                                onClick={() => handleChangeStory(opt)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                    story === opt
                                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                        : 'text-blue-200 hover:text-white hover:bg-white/10'
                                }`}
                            >
                                {opt}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Lyrics Panel */}
                    <div className="lg:col-span-2">
                        <div className="bg-[#1a2d4a] rounded-2xl p-6 shadow-xl border border-white/5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-white font-semibold flex items-center gap-2">
                                    <SoundOutlined className="text-blue-400" /> Lyrics / Transcript
                                </h3>
                                    <div className="flex items-center gap-3">
                                        <span className="text-blue-300 text-sm bg-blue-500/10 px-3 py-1 rounded-full">
                                            Câu {active + 1}/{ts.length || 0}
                                        </span>
                                    </div>
                            </div>

                            {/* LRC Display */}
                            <div ref={lyricsContainerRef} className="bg-[#0f1e35] rounded-xl p-4 max-h-[48vh] md:max-h-[60vh] lg:max-h-[520px] overflow-y-auto scrollbar-thin">
                                {lyric?.map((line, i) => (
                                        <div
                                            key={i}
                                            data-lyric-index={i}
                                            onClick={() => playSentenceOnly(i)}
                                            className={`px-4 py-3 rounded-lg cursor-pointer mb-1 transition-all ${
                                                (active === i || playingSentence === i)
                                                    ? 'bg-blue-500/20 border-l-4 border-blue-400 text-yellow-300'
                                                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5 border-l-4 border-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                {/* Container for text + IPA + translation */}
                                                <div className="flex-1 min-w-0">
                                                    {/* IPA - shown above, only for active/playing sentence */}
                                                    {showIPA && (active === i || playingSentence === i) && (
                                                        <div className="mb-1 text-cyan-400/80 font-mono" style={{ fontSize: `${Math.max(subtitleFontSize - 4, 10)}px` }}>
                                                            {(getIpaForIndex(i) === null) ? (
                                                                <span className="opacity-40 animate-pulse">● ● ● ● ●</span>
                                                            ) : (
                                                                (getIpaForIndex(i) || <span className="opacity-30">—</span>)
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Lyric text — hidden/blurred when hideSubtitle + this sentence is playing; press to temporarily reveal */}
                                                    {/* If this lesson needs numbering at line start, prefix number here */}
                                                    <span
                                                        className={`${active === i ? 'font-semibold' : ''} transition-all select-none flex items-center`}
                                                        style={{ fontSize: `${subtitleFontSize}px` }}
                                                    >
                                                        {slug === 'bai-01-day-of-the-dead' && (
                                                            <span className="text-xs text-gray-400 mr-3 flex-shrink-0">{i + 1}.</span>
                                                        )}
                                                        {((hiddenLines[i] === false) || (showTranscript && !hiddenLines[i])) ? line : <span className="opacity-40 italic text-gray-500">(đã ẩn)</span>}
                                                    </span>

                                                    {/* Translation - shown below each sentence when enabled */}
                                                    {showTranslation && ((hiddenLines[i] === false) || (showTranscript && !hiddenLines[i])) && (
                                                        <div className="mt-1 text-yellow-300/70 italic" style={{ fontSize: `${translationFontSize}px` }}>
                                                            {translationLoading ? (
                                                                <span className="opacity-40 animate-pulse">Đang dịch...</span>
                                                            ) : (
                                                                translations[i] || <span className="opacity-30">—</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Right-side: show index and eye icon when transcript hidden */}
                                                <div className="flex items-center gap-2 ml-3">
                                                    {/* Show index on the right for lessons except bai-01-day-of-the-dead (which uses prefixed numbering) */}
                                                    {slug !== 'bai-01-day-of-the-dead' && (
                                                        <span className="text-xs text-gray-500 flex-shrink-0">{i + 1}</span>
                                                    )}
                                                    {/* Eye icon always visible — click toggles transcript for the line */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setHiddenLines(prev => ({ ...prev, [i]: prev[i] === undefined ? false : !prev[i] }));
                                                        }}
                                                        className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 text-gray-300 hover:bg-white/10 ml-2"
                                                        title="Nhấn để hiện/ẩn transcript"
                                                    >
                                                        {((hiddenLines[i] === false) || (showTranscript && !hiddenLines[i])) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                        </div>
                    </div>

                    {/* Right: Controls Panel */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Action Buttons */}
                        <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Công cụ</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <Tooltip title="Chuyển sang trang luyện nói">
                                    <button
                                        onClick={() => {
                                            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
                                            const hasVipAccess = (dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD") && vipExpireAt !== null);
                                            if (!hasVipAccess) {
                                                message.warning('Chức năng này dành cho người dùng VIP. Vui lòng nâng cấp tài khoản!');
                                                return;
                                            }
                                            navigate(`/lesson/speaking/${slug}`, { state: { index } });
                                        }}
                                        className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-green-500/10 text-green-400 hover:bg-green-500/20"
                                    >
                                        <span className="text-lg">🗣️</span> Luyện nói
                                    </button>
                                </Tooltip>
                                <Tooltip title="Luyện nghe từng câu — nghe audio từng câu, có Prev/Next">
                                    <button
                                        onClick={() => {
                                            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
                                            const hasVipAccess = (dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD") && vipExpireAt !== null);
                                            if (!hasVipAccess) {
                                                message.warning('Chức năng này dành cho người dùng VIP. Vui lòng nâng cấp tài khoản!');
                                                return;
                                            }
                                            openDrillMode();
                                        }}
                                        className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20"
                                    >
                                        <span className="text-lg">🎧</span> Luyện nghe
                                    </button>
                                </Tooltip>
                                <button onClick={() => setShowSettings(!showSettings)}
                                    className="col-span-2 flex items-center gap-2 bg-blue-500/10 text-blue-400 px-4 py-3 rounded-xl hover:bg-blue-500/20 transition-all text-sm font-medium w-full">
                                    <SettingOutlined className="text-lg" /> Cài đặt
                                </button>
                            </div>
                        </div>

                        {/* Settings Panel */}
                        {showSettings && (
                            <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Cài đặt</h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-gray-400 text-xs mb-2">Tốc độ phát</p>
                                        <div className="flex flex-wrap gap-2">
                                            {speeds.map(s => (
                                                <button key={s} onClick={() => changeSpeedMode(s)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                        speedMode === s
                                                            ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                                    }`}>
                                                    {s}x
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 text-xs mb-2">Lặp lại: {replay - 1 > 0 ? replay - 1 : 0} lần</p>
                                        <Slider min={1} max={11} value={replay} onChange={v => setReplay(v)}
                                            className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-gray-400 text-xs mb-2">Nghỉ giữa lần lặp: {timeReplay}s</p>
                                        <Slider min={0} max={3} step={0.5} value={timeReplay} onChange={v => setTimeReplay(v)}
                                            className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" />
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-white/10 my-2"></div>

                                    {/* Auto Scroll Toggle */}
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                            <RetweetOutlined /> Tự động cuộn
                                        </p>
                                        <Switch
                                            size="small"
                                            checked={autoScroll}
                                            onChange={v => setAutoScroll(v)}
                                            className={autoScroll ? '!bg-blue-500' : ''}
                                        />
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-white/10 my-2"></div>

                                    {/* Font Size Controls */}
                                    <div>
                                        <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                            <FontSizeOutlined /> Cỡ chữ phụ đề: {subtitleFontSize}px
                                        </p>
                                        <Slider 
                                            min={12} 
                                            max={28} 
                                            step={1} 
                                            value={subtitleFontSize} 
                                            onChange={v => setSubtitleFontSize(v)}
                                            className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" 
                                        />
                                    </div>

                                    <div>
                                        <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                            <FontSizeOutlined /> Cỡ chữ bản dịch: {translationFontSize}px
                                        </p>
                                        <Slider 
                                            min={10} 
                                            max={24} 
                                            step={1} 
                                            value={translationFontSize} 
                                            onChange={v => setTranslationFontSize(v)}
                                            className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" 
                                        />
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-white/10 my-2"></div>

                                    {/* Translations and IPA are always shown on listening page */}
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                            <EyeOutlined /> Hiển thị bản dịch
                                        </p>
                                        <Switch
                                            size="small"
                                            checked={showTranslation}
                                            onChange={v => setShowTranslation(v)}
                                            className={showTranslation ? '!bg-blue-500' : ''}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between mt-2">
                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                            <EyeInvisibleOutlined /> Hiển thị IPA
                                        </p>
                                        <Switch
                                            size="small"
                                            checked={showIPA}
                                            onChange={v => setShowIPA(v)}
                                            className={showIPA ? '!bg-blue-500' : ''}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between mt-2">
                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                            <EyeOutlined /> Hiển thị Transcript
                                        </p>
                                        <Switch
                                            size="small"
                                            checked={showTranscript}
                                            onChange={v => setShowTranscript(v)}
                                            className={showTranscript ? '!bg-blue-500' : ''}
                                        />
                                    </div>
                                    <div className="border-t border-white/10 my-2"></div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* Bottom Audio Controls (fixed) */}
                <div className="fixed bottom-0 left-0 right-0 bg-[#0a1628]/95 backdrop-blur-xl border-t border-white/10 z-30">
                    {/* Progress bar — full width */}
                    <div className="max-w-screen-xl mx-auto px-3 pt-2">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs font-mono w-9 text-right flex-shrink-0">
                                {formatTime(currentTime / 1000)}
                            </span>
                            <input
                                type="range"
                                min={0}
                                max={totalDuration || 0}
                                step={0.1}
                                value={currentTime / 1000}
                                onMouseDown={() => { isSeekingRef.current = true; }}
                                onTouchStart={() => { isSeekingRef.current = true; }}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    if (audioElem.current && isFinite(val)) {
                                        audioElem.current.currentTime = val;
                                        setcurrentTime(val * 1000);
                                        // Update active sentence to match seeked position
                                        const tsArr = timestamps[toLowerCamelCase(story)] || [];
                                        let newActive = 0;
                                        for (let i = 0; i < tsArr.length; i++) {
                                            if (val * 1000 >= tsArr[i]) newActive = i;
                                            else break;
                                        }
                                        setActive(newActive);
                                    }
                                }}
                                onMouseUp={() => { isSeekingRef.current = false; }}
                                onTouchEnd={() => { isSeekingRef.current = false; }}
                                className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                                style={{
                                    background: totalDuration
                                        ? `linear-gradient(to right, #3b82f6 ${((currentTime / 1000) / totalDuration) * 100}%, rgba(255,255,255,0.1) ${((currentTime / 1000) / totalDuration) * 100}%)`
                                        : 'rgba(255,255,255,0.1)',
                                    accentColor: '#3b82f6',
                                }}
                            />
                            <span className="text-gray-400 text-xs font-mono w-9 flex-shrink-0">
                                {formatTime(totalDuration || 0)}
                            </span>
                        </div>
                    </div>

                    {/* Controls row */}
                    <div className="max-w-screen-xl mx-auto px-3 py-2">
                        <div className="flex items-center gap-2">

                            {/* LEFT: Speed button — always visible far left */}
                            <button
                                onClick={() => {
                                    const idx = speeds.indexOf(speedMode);
                                    changeSpeedMode(speeds[(idx + 1) % speeds.length]);
                                }}
                                className="bg-white/10 text-blue-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-white/20 transition-all flex-shrink-0 min-w-[44px] text-center"
                            >
                                {speedMode}x
                            </button>

                            {/* CENTER: Prev / Play / Next — takes remaining space, centered */}
                            <div className="flex-1 flex items-center justify-center gap-4 sm:gap-6">
                                <button
                                    onClick={() => { setActive(Math.max(0, active - 1)); pause(); }}
                                    className="text-gray-300 hover:text-white p-2 transition-colors"
                                >
                                    <StepBackwardOutlined className="text-xl" />
                                </button>
                                <button
                                    onClick={PlayPause}
                                    className="bg-blue-500 hover:bg-blue-600 text-white w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30 transition-all hover:scale-105 flex-shrink-0"
                                >
                                    {isPlaying
                                        ? <PauseCircleFilled className="text-xl sm:text-2xl" />
                                        : <PlayCircleFilled className="text-xl sm:text-2xl" />}
                                </button>
                                <button
                                    onClick={() => { setActive((active + 1) % (ts.length || 1)); pause(); }}
                                    className="text-gray-300 hover:text-white p-2 transition-colors"
                                >
                                    <StepForwardOutlined className="text-xl" />
                                </button>
                            </div>

                            {/* RIGHT: Loop + Volume */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {/* Loop button */}
                                <div className="relative">
                                    <button
                                        onClick={() => setLoopMode((loopMode + 1) % 3)}
                                        title={loopMode === 0 ? 'Ko lặp' : loopMode === 1 ? 'Lặp 1 câu' : 'Lặp cả bài'}
                                        className={`rounded-md p-2 transition-all flex items-center justify-center ${
                                            loopMode === 2 ? 'bg-white/10 text-white shadow-lg' : (loopMode === 1 ? 'bg-transparent text-white' : 'bg-transparent text-gray-300 opacity-40')
                                        }`}
                                    >
                                        <img src={repeatTrack} alt="repeat" className="w-5 h-5" />
                                    </button>
                                    {loopMode === 1 && (
                                        <div className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-extrabold rounded-full w-5 h-5 flex items-center justify-center shadow-lg border border-white/10">1</div>
                                    )}
                                </div>
                                {/* Volume — hidden on mobile, visible sm+ */}
                                <div className="flex items-center gap-1.5">
                                    <AudioFilled className="text-gray-400 text-sm" />
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.01}
                                        value={volume}
                                        onChange={(e) => setVolume(parseFloat(e.target.value))}
                                        className="w-20 h-1 accent-blue-500"
                                        aria-label="Volume"
                                    />
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
                </>
                )}
            </div>

            

            

            {/* Score Results Modal */}
            <Modal
                open={showScoreModal}
                onCancel={() => setShowScoreModal(false)}
                footer={[
                    <Button key="close" onClick={() => setShowScoreModal(false)}
                        className="!bg-blue-500 !text-white !border-blue-500 hover:!bg-blue-600">
                        Đóng
                    </Button>
                ]}
                title={
                    <span className="text-white font-bold text-lg">Kết quả luyện nói</span>
                }
                className="[&_.ant-modal-content]:bg-[#1a2d4a] [&_.ant-modal-content]:border [&_.ant-modal-content]:border-white/10 [&_.ant-modal-header]:bg-transparent [&_.ant-modal-title]:text-white [&_.ant-modal-close]:text-gray-400 [&_.ant-modal-footer]:bg-transparent [&_.ant-modal-footer]:border-t [&_.ant-modal-footer]:border-white/10"
                width={600}
            >
                {scoreResult && (
                    <div>
                        {/* Overall score */}
                        <div className="flex items-center justify-center gap-8 mb-5 py-5 bg-[#0f1e35] rounded-xl border border-white/5">
                            <Progress
                                type="circle"
                                size={88}
                                percent={scoreResult.avgPoint}
                                strokeColor={
                                    scoreResult.avgPoint >= 80 ? '#22c55e'
                                    : scoreResult.avgPoint >= 50 ? '#f59e0b'
                                    : '#ef4444'
                                }
                                trailColor="rgba(255,255,255,0.1)"
                                format={p => <span className="text-white font-bold text-lg">{p}%</span>}
                            />
                            <div className="text-center">
                                <p className="text-gray-400 text-sm mb-1">Điểm trung bình</p>
                                <p className={`text-3xl font-bold mb-1 ${scoreResult.avgPoint >= 80 ? 'text-green-400' : scoreResult.avgPoint >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                                    {scoreResult.avgPoint}%
                                </p>
                                <p className="text-gray-400 text-xs">{scoreResult.totalLines} câu</p>
                            </div>
                        </div>

                        {/* Per-line results */}
                        <div className="max-h-[320px] overflow-y-auto space-y-2 pr-1
                            [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
                            {scoreResult.lines.map((line, i) => (
                                <div key={i} className="bg-[#0f1e35] rounded-lg px-4 py-3 border border-white/5">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-gray-400 text-xs font-medium">Câu {i + 1}</span>
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                            line.point >= 80 ? 'bg-green-500/20 text-green-400'
                                            : line.point >= 50 ? 'bg-yellow-500/20 text-yellow-400'
                                            : 'bg-red-500/20 text-red-400'
                                        }`}>
                                            {line.point}%
                                        </span>
                                    </div>
                                    {/* Lyric gốc */}
                                    <p className="text-gray-300 text-xs mb-1.5 leading-relaxed">{line.lyric}</p>
                                    {/* Lời ghi âm — từng từ highlight */}
                                    <div className="flex flex-wrap gap-x-1 gap-y-0.5 bg-black/20 rounded px-2 py-1.5">
                                        <span className="text-[10px] text-gray-500 mr-0.5 self-center">🎤</span>
                                        {line.spokenLine?.length > 0
                                            ? line.spokenLine.map(({ word, matched }, wi) => (
                                                <span key={wi} className={`font-semibold text-sm ${matched ? 'text-green-400' : 'text-red-400'}`}>
                                                    {word}
                                                </span>
                                            ))
                                            : <span className="text-gray-500 italic text-xs">Không nhận được giọng</span>
                                        }
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="text-gray-500 text-xs mt-3 text-center">
                            <span className="text-green-400 font-semibold">Xanh</span> = đúng &nbsp;·&nbsp;
                            <span className="text-red-400 font-semibold">Đỏ</span> = sai / thiếu
                        </p>
                    </div>
                )}
            </Modal>

            {/* Drill Video Recording Summary Modal */}
            <Modal
                open={showDrillRecordingModal}
                onOk={() => setShowDrillRecordingModal(false)}
                onCancel={() => setShowDrillRecordingModal(false)}
                width={600}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setShowDrillRecordingModal(false)}
                        className="bg-blue-500">
                        Đóng
                    </Button>
                ]}
            >
                <div className="py-4">
                    <h2 className="text-2xl font-bold text-center mb-4 text-blue-600">
                        🎥 Kết quả ghi hình
                    </h2>
                    
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-4">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <CheckCircleOutlined className="text-green-500 text-3xl" />
                            <p className="text-lg font-bold text-gray-800">Hoàn thành ghi hình!</p>
                        </div>
                        <p className="text-center text-gray-600 text-sm">
                            Video đã được lưu vào máy của bạn
                        </p>
                    </div>

                    {drillRecordingTranscript && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <SoundOutlined className="text-blue-500" />
                                Lời bạn đã nói:
                            </h3>
                            <p className="text-gray-800 leading-relaxed">
                                {drillRecordingTranscript || <span className="text-gray-400 italic">Không phát hiện lời nói</span>}
                            </p>
                        </div>
                    )}

                    <div className="mt-4 text-center">
                        <p className="text-sm text-gray-600">
                            💪 Tiếp tục luyện tập để cải thiện kỹ năng!
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
